/**
 * S1 — Agent catalog (`/ai-studio/agents`).
 *
 * UX1: Run from agent (silent pin). Copy only Public or own agents.
 * Draft cannot run; Archived may run with confirm.
 * FlexibleCRUD list + compact catalog filters (state, scope, domain, tenant, creator, dates).
 * Default list excludes archived agents; use State filter → Archived to show them.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArchiveBoxArrowDownIcon,
  ArchiveBoxXMarkIcon,
  ArrowPathIcon,
  CubeTransparentIcon,
} from '@heroicons/react/24/outline';
import {
  AgentCatalogFilterToggle,
  AgentCatalogFilterPanel,
  EMPTY_AGENT_CATALOG_FILTERS,
  agentFiltersToQueryParams,
} from '@/components/ai-studio/agents';
import type { AgentCatalogFilterValues } from '@/components/ai-studio/agents';
import { FlexibleCRUDTable } from '@/components/common/FlexibleCRUDTable';
import { aiStudioAgentEntity } from '@/config/entities/aiStudioAgents';
import { aiStudioAgentsCRUDService } from '@/services/aiStudioAgentsCRUD';
import {
  createAgent,
  copyAgent,
  deleteAgent,
  updateAgent,
} from '@/services/aiStudioAgentService';
import { useAuth } from '@/state/auth/AuthProvider';
import { useLanguage } from '@/state/language/LanguageProvider';
import type { AiStudioAgent, StudioAgentState } from '@/types/aiStudioAgent';

function agentState(agent: AiStudioAgent): StudioAgentState {
  return agent.visibility;
}

function canCopyAgent(agent: AiStudioAgent, isPlatformAdmin: boolean): boolean {
  if (!isPlatformAdmin) return false;
  if (agent.visibility === 'public') return true;
  if (agent.isPlatform) return true;
  return false;
}

function isPlatformAdministrator(role: string | undefined): boolean {
  return role === 'Platform Administrator' || role === 'PLATFORM_ADMIN';
}

const AIStudioAgentCatalogPage: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const isPlatformAdmin = isPlatformAdministrator(user?.role);
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [tableKey, setTableKey] = useState(0);
  const [filters, setFilters] = useState<AgentCatalogFilterValues>(EMPTY_AGENT_CATALOG_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const extraQueryParams = useMemo(() => agentFiltersToQueryParams(filters), [filters]);

  const openDefinition = useCallback(
    (agent: AiStudioAgent) => {
      navigate(`/ai-studio/agents/${agent.id}`);
    },
    [navigate]
  );

  const openHistory = useCallback(
    (agent: AiStudioAgent) => {
      navigate(`/ai-studio/agents/${agent.id}/versions`);
    },
    [navigate]
  );

  const handleCreate = useCallback(async () => {
    if (creating) return;
    setCreating(true);
    setErrorKey(null);
    try {
      const { agent } = await createAgent({
        name: t('aiStudio.agents.untitledName'),
        visibility: 'draft',
        // Factory agents are platform catalog agents (REQ-AIS-117); omit false — backend rejects is_platform=false.
      });
      navigate(`/ai-studio/agents/${agent.id}`);
    } catch (e) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      setErrorKey('aiStudio.agents.createFailed');
      setErrorDetail(typeof msg === 'string' ? msg : null);
      setCreating(false);
    }
  }, [creating, navigate, t]);

  const handleCopy = useCallback(
    async (agent: AiStudioAgent) => {
      setErrorKey(null);
      setErrorDetail(null);
      if (!canCopyAgent(agent, isPlatformAdmin)) {
        setErrorKey('aiStudio.agents.copyNotAllowed');
        return;
      }
      try {
        const { agent: draft } = await copyAgent(agent.id);
        navigate(`/ai-studio/agents/${draft.id}`);
      } catch (e) {
        const msg =
          e && typeof e === 'object' && 'response' in e
            ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
            : null;
        setErrorKey('aiStudio.agents.copyFailed');
        setErrorDetail(typeof msg === 'string' ? msg : null);
      }
    },
    [navigate, isPlatformAdmin]
  );

  const handleDeploy = useCallback(
    (agent: AiStudioAgent) => {
      setErrorKey(null);
      setErrorDetail(null);
      if (agentState(agent) === 'draft') {
        setErrorKey('aiStudio.agents.draftCannotDeploy');
        return;
      }
      navigate(
        `/ai-studio/instances/provision?agentId=${encodeURIComponent(agent.id)}`
      );
    },
    [navigate]
  );

  const handleDelete = useCallback(async (agent: AiStudioAgent) => {
    if (agentState(agent) !== 'draft') return;
    setErrorKey(null);
    try {
      await deleteAgent(agent.id);
      setTableKey((key) => key + 1);
    } catch {
      setErrorKey('aiStudio.agents.deleteFailed');
    }
  }, []);

  const setState = useCallback(
    async (agent: AiStudioAgent, visibility: StudioAgentState) => {
      setErrorKey(null);
      try {
        await updateAgent(agent.id, { visibility });
      } catch {
        setErrorKey('aiStudio.agents.detail.saveFailed');
        throw new Error('state update failed');
      }
    },
    []
  );

  const handleArchive = useCallback(
    async (agent: AiStudioAgent) => {
      const state = agentState(agent);
      if (state !== 'private' && state !== 'public') return;
      const confirmed = window.confirm(t('aiStudio.agents.archiveConfirm', { name: agent.name }));
      if (!confirmed) return;
      await setState(agent, 'archived');
    },
    [setState, t]
  );

  const handleUnarchive = useCallback(
    async (agent: AiStudioAgent) => {
      if (agentState(agent) !== 'archived') return;
      await setState(agent, 'private');
    },
    [setState]
  );

  return (
    <main className="flex w-full flex-col gap-4 px-4 py-4 md:px-6 lg:px-8">
      <header>
        <h1 className="text-lg font-semibold text-[var(--color-surface-text)]">
          {t('aiStudio.agents.title')}
        </h1>
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.agents.subtitle')}
        </p>
      </header>

      {errorKey && (
        <div
          role="alert"
          className="rounded border border-[var(--color-error)] bg-[var(--color-surface-bg)] px-4 py-2 text-base text-[var(--color-error)]"
        >
          <p>{errorDetail ?? t(errorKey)}</p>
        </div>
      )}

      <FlexibleCRUDTable
        key={`${tableKey}-${JSON.stringify(extraQueryParams)}`}
        entity={aiStudioAgentEntity}
        service={aiStudioAgentsCRUDService}
        showCreateButton={isPlatformAdmin}
        showSearch
        showPagination
        pageSize={20}
        searchPlaceholder={t('aiStudio.agents.searchLabel')}
        useConfirmDialog
        extraQueryParams={extraQueryParams}
        toolbarAfterSearch={
          <AgentCatalogFilterToggle
            open={filtersOpen}
            onOpenChange={setFiltersOpen}
            value={filters}
            onChange={setFilters}
          />
        }
        belowToolbar={
          filtersOpen ? (
            <AgentCatalogFilterPanel value={filters} onChange={setFilters} />
          ) : null
        }
        onCreate={() => {
          void handleCreate();
        }}
        onView={openHistory}
        onEdit={(agent: AiStudioAgent) => {
          if (agentState(agent) === 'draft') openDefinition(agent);
        }}
        onCopy={(agent: AiStudioAgent) => handleCopy(agent)}
        onDelete={(agent: AiStudioAgent) => handleDelete(agent)}
        canEdit={(agent: AiStudioAgent) => isPlatformAdmin && agentState(agent) === 'draft'}
        canCopy={(agent: AiStudioAgent) => canCopyAgent(agent, isPlatformAdmin)}
        canDelete={(agent: AiStudioAgent) => isPlatformAdmin && agentState(agent) === 'draft'}
        customActions={[
          {
            label: t('aiStudio.instances.deployInstance'),
            icon: CubeTransparentIcon,
            variant: 'secondary',
            visible: (agent: AiStudioAgent) => agentState(agent) !== 'draft',
            onClick: (agent: AiStudioAgent) => {
              handleDeploy(agent);
            },
          },
          {
            label: t('aiStudio.agents.archive'),
            icon: ArchiveBoxArrowDownIcon,
            variant: 'danger',
            visible: (agent: AiStudioAgent) => {
              const state = agentState(agent);
              return state === 'private' || state === 'public';
            },
            onClick: (agent: AiStudioAgent) => handleArchive(agent),
          },
          {
            label: t('aiStudio.agents.togglePrivate'),
            icon: ArrowPathIcon,
            variant: 'secondary',
            visible: (agent: AiStudioAgent) => agentState(agent) === 'public',
            onClick: (agent: AiStudioAgent) => setState(agent, 'private'),
          },
          {
            label: t('aiStudio.agents.togglePublic'),
            icon: ArrowPathIcon,
            variant: 'secondary',
            visible: (agent: AiStudioAgent) => agentState(agent) === 'private',
            onClick: (agent: AiStudioAgent) => setState(agent, 'public'),
          },
          {
            label: t('aiStudio.agents.unarchive'),
            icon: ArchiveBoxXMarkIcon,
            variant: 'secondary',
            visible: (agent: AiStudioAgent) => agentState(agent) === 'archived',
            onClick: (agent: AiStudioAgent) => handleUnarchive(agent),
          },
        ]}
        onRowClick={(agent: AiStudioAgent) => {
          if (agentState(agent) === 'draft') {
            openDefinition(agent);
          } else {
            openHistory(agent);
          }
        }}
      />
    </main>
  );
};

export default AIStudioAgentCatalogPage;
